import axios from 'axios'
import _ from 'lodash';

function normalizeFilter(filter) {
    if (_.isString(filter)) {
        return [{
            op: 'query',
            value: filter,
        }]
    } else if (Array.isArray(filter)) {
        let filters = []
        for (let singleFilter of filter) {
            filters.push(normalizeFilter(singleFilter))
        }
        return filters.flat()
    } else {
        let filters = []
        for (let field in filter) {
            if (field === '$q' || field === '$query') {
                filters.push({
                    op: 'query',
                    value: filter[field],
                })
            } else {
                let normalized = {
                    field: field,
                }
                if (typeof filter[field] === 'string') {
                    normalized.value = filter[field]
                } else if (Array.isArray(filter[field])) {
                    normalized.value = filter[field].join(',')
                    normalized.op = 'in'
                } else {
                    normalized.value = filter[field].value
                    normalized.op = filter[field].op
                }
                if (filter.op === '=' || filter.op === 'eq') delete filter.op
                filters.push(normalized)
            }
        }
        return filters
    }
}

function buildFilterStr(filters) {
    if (!filters) return ''
    filters = normalizeFilter(filters)
    let filterStrings = []
    for (let filter of filters) {
        if (filter.op === 'query') {
            filterStrings.push('q=' + filter.value)
        } else {

            let filterString = `filter[${filter.field}]`
            if (filter.op && filter.op !== '=' && filter.op !== 'eq') {
                filterString += `[${filter.op}]`
            }
            if (filter.value) {
                filterString += `=${filter.value}`
            }
            filterStrings.push(filterString)
        }
    }
    return filterStrings.join('&')
}

function buildFieldStr(fields) {
    if (Array.isArray(fields) && fields.length > 0) {
        return 'fields=' + fields.join(',')
    } else if (typeof fields === 'string') {
        return 'fields=' + fields
    } else {
        return null
    }
}

function buildGetParams() {
    let params = Array.from(arguments).filter(a => !!a);
    return params.length > 0 ? '?' + params.join('&') : ''
}

function toTitleCase(str) {
    return str.replace(
        /\w\S*/g,
        function (txt) {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        }
    );
}

function getSeconds() {
    return new Date().getTime() / 1000
}

// function recurseFields(fields, prefix, depth = 1) {
//     let recursed = fields.slice()
//     if (depth > 0) {
//         for (let field of fields) {
//             recursed.push(prefix + '.' + field)
//         }
//         if (depth > 1) recursed = recurseFields(recursed, prefix, depth - 1)
//     }
//     return recursed
// }

export default class Collection {
    $directus
    name
    title
    titleSingular
    fields
    customParse
    cacheTtl
    requestCache
    idFields
    idCache

    constructor(directus, name, config={}) {
        this.$directus = directus
        this.name = name.trim()
        if (config.title) {
            let titles = config.title.split('|')
            this.title = titles[0].trim()
            if (titles.length > 1) {
                this.titleSingular = titles[1].trim()
            }
        } else {
            this.title = toTitleCase(this.name)
        }
        if (!this.titleSingular) this.titleSingular = this.title
        this.fields = config.fields
        this.relations = config.relations
        this.customParse = config.parse
        this.cacheTtl = config.cacheTtl || 0
        this.cacheCheckInterval = config.cacheCheckInterval || this.cacheTtl * 2
        this.idFields = config.idFields
        this.requestCache = {}
        this.idCache = {}

        if (this.cacheTtl > 0 && isFinite(this.cacheTtl)) {
            setTimeout(() => this.cacheCheck(), this.cacheCheckInterval * 1000)
        }
    }

    cacheCheck() {
        let threshold = getSeconds() - this.cacheTtl
        for (let requestStr in this.requestCache) {
            if (this.requestCache[requestStr].time < threshold) {
                delete this.requestCache[requestStr]
            }
        }
    }

    parse(item, relationsOverride) {
        if (!item) return item
        if (Array.isArray(item)) {
            return item.map(i => this.parse(i)).filter(i => i !== null)
        }
        if (typeof item === 'object') {
            let relations = relationsOverride || this.relations
            if (relations) {
                for (let localRelationField in relations) {
                    if (item[localRelationField] !== undefined) {
                        let relation = relations[localRelationField]
                        let relatedCollection = this.$directus[relation.collection]
                        if (!relatedCollection) throw new Error('unknown collection: ' + relation.collection)
                        let relationType = relation.type.toLowerCase()

                        if (relationType === 'manytoone') {
                            if (relation.fetch === 'embed') {
                                item[localRelationField] = relatedCollection.parse(item[localRelationField], relation.relations)
                            } else if (relation.fetch === 'eager') {
                                if (item[localRelationField]) item[localRelationField] = Promise.resolve(relatedCollection.parse(item[localRelationField], relation.relations))
                            } else if (relation.fetch === 'lazy_batch') {
                                // todo
                            } else if (relation.fetch === 'lazy') {
                                if (item[localRelationField]) item[localRelationField] = relatedCollection.fetchLazy(item[localRelationField], relation.fields, relation.relations, relation.enableCache)
                            }


                        } else if (relationType === 'onetomany') {
                            if (relation.fetch === 'embed') {
                                item[localRelationField] = relatedCollection.parse(item[localRelationField], relation.relations)
                            } else if (relation.fetch === 'eager') {
                                item[localRelationField] = item[localRelationField]
                                    .map(i => relatedCollection.parse(i, relation.relations))
                                    .filter(i => i !== null)
                                    .map(i => Promise.resolve(i))
                            } else if (relation.fetch === 'lazy_batch') {
                                // todo
                            } else if (relation.fetch === 'lazy') {
                                item[localRelationField] = item[localRelationField]
                                    .map(i => relatedCollection.fetchLazy(i.id, relation.fields, relation.relations, relation.enableCache))
                            }


                        } else if (relationType === 'manytomany') {
                            if (relation.fetch === 'embed') {
                                item[localRelationField] = item[localRelationField]
                                    .map(j => relatedCollection.parse(j[relation.joinField], relation.relations))
                                    .filter(i => i !== null)
                            } else if (relation.fetch === 'eager') {
                                item[localRelationField] = item[localRelationField]
                                    .map(j => relatedCollection.parse(j[relation.joinField], relation.relations))
                                    .filter(i => i !== null)
                                    .map(i => Promise.resolve(i))
                            } else if (relation.fetch === 'lazy_batch') {
                                // todo
                            } else if (relation.fetch === 'lazy') {
                                item[localRelationField] = item[localRelationField]
                                    .map(j => relatedCollection.fetchLazy(j[relation.joinField], relation.fields, relation.relations, relation.enableCache))
                            }
                        }
                    }
                }
            }
        }

        if (typeof this.customParse === 'function') {
            let result = this.customParse(item, this, this.$directus)
            if (result === undefined) {
                return item
            } else if (result === false) {
                return null
            } else {
                return result
            }
        }
        return item
    }

    getRelations(relationsOverride) {
        let relations = this.relations ? _.cloneDeep(this.relations): {}
        if (relationsOverride) {
            for (let relationField in relationsOverride) {
                let overrideRelation = relationsOverride[relationField]
                if (!overrideRelation) {
                    delete relations[relationField]
                } else if (typeof overrideRelation === 'string') {
                    relations[relationField].fetch = overrideRelation
                } else if (typeof overrideRelation === 'object') {
                    Object.assign(relations[relationField], overrideRelation);
                }
            }
        }
        return relations
    }

    getFields(fieldsOverride, relationsOverride, remainingRecursions={}) {
        let fields = (fieldsOverride ? fieldsOverride.slice() : false) || (this.fields ? this.fields.slice() : [])
        let relations = this.getRelations(relationsOverride)
        if (Object.keys(relations).length > 0) {
            if (fields.length === 0) {
                fields.push('*')
            } else {
                for (let relationField in this.relations) {
                    if (fields.includes(relationField)) {
                        relations[relationField] = this.relations[relationField]
                        fields.splice(fields.indexOf(relationField), 1)
                    } else {
                        delete relations[relationField]
                    }
                }
            }

            for (let localRelationField in relations) {
                let relation = relations[localRelationField]
                let relatedCollection = this.$directus[relation.collection]
                if (!relatedCollection) throw new Error('unknown collection: ' + relation.collection)
                let relationFields = []
                if (!remainingRecursions[this.name] || remainingRecursions[this.name][localRelationField] === undefined || remainingRecursions[this.name][localRelationField] >= 1) {
                    let remainingRecursionsCopy = _.cloneDeep(remainingRecursions)
                    if (!remainingRecursionsCopy[this.name]) {
                        remainingRecursionsCopy[this.name] = {}
                        remainingRecursionsCopy[this.name][localRelationField] = relation.recursionDepth === undefined ? 1 : relation.recursionDepth
                    }
                    remainingRecursionsCopy[this.name][localRelationField]--
                    relationFields = relatedCollection.getFields(relation.fields, relation.relations, remainingRecursionsCopy)
                }
                if (!relationFields || relationFields.length === 0) relationFields = ['*']

                let relationType = relation.type.toLowerCase()
                if (relationType === 'manytoone') {
                    if (relation.fetch === 'embed' || relation.fetch === 'eager') {
                        for (let relationField of relationFields) {
                            fields.push(localRelationField + '.' + relationField)
                        }
                    } else if (relation.fetch === 'trigger_lazy' || relation.fetch === 'lazy') {
                        fields.push(localRelationField)
                    }
                } else if (relationType === 'onetomany') {
                    if (relation.fetch === 'embed' || relation.fetch === 'eager') {
                        for (let relationField of relationFields) {
                            fields.push(localRelationField + '.' + relationField)
                        }
                    } else if (relation.fetch === 'trigger_lazy' || relation.fetch === 'lazy') {
                        fields.push(localRelationField + '.id')
                    }
                } else if (relationType === 'manytomany') {
                    if (relation.fetch === 'embed' || relation.fetch === 'eager') {
                        for (let relationField of relationFields) {
                            fields.push(localRelationField + '.' + relation.joinField + '.' + relationField)
                        }
                    } else if (relation.fetch === 'trigger_lazy' || relation.fetch === 'lazy') {
                        fields.push(localRelationField + '.' + relation.joinField)
                    }
                }
            }
        }
        return fields
    }

    buildRequestStr(filters, fieldsOverride, relationsOverride) {
        let fields = this.getFields(fieldsOverride, relationsOverride)
        if (_.isInteger(filters)) {
            return this.$directus.$endpoint + '/items/' + this.name + '/' + filters + buildGetParams(null, buildFieldStr(fields))
        } else {
            return this.$directus.$endpoint + '/items/' + this.name + buildGetParams(buildFilterStr(filters), buildFieldStr(fields))
        }
    }

    updateIdCache(item) {
        if (this.cacheTtl > 0) {
            Promise.resolve(item).then(item => {
                if (Array.isArray(item)) {
                    item.forEach(i => this.updateIdCache(i))
                } else {
                    for (let idField of this.idFields) {
                        if (!this.idCache[idField]) this.idCache[idField] = {}
                        this.idCache[idField][item[idField]] = {
                            time: getSeconds(),
                            promise: Promise.resolve(item),
                        }
                    }
                }
            })
        }
    }

    updateRequestCache(requestStr, promise) {
        if (this.cacheTtl > 0) {
            this.requestCache[requestStr] = {
                time: getSeconds(),
                promise: promise,
            }
        }
    }

    idCacheLookup(idField, value) {
        if (this.cacheTtl > 0 && this.idCache[idField]) {
            let hit = this.idCache[idField][value]
            if (hit) {
                if (hit.time >= getSeconds() - this.cacheTtl) {
                    return hit.promise
                } else {
                    delete this.idCache[idField][value]
                }
            }
        }
        return null
    }

    requestCacheLookup(requestStr) {
        if (this.cacheTtl > 0) {
            let hit = this.requestCache[requestStr]
            if (hit) {
                if (hit.time >= getSeconds() - this.cacheTtl) {
                    return hit.promise
                } else {
                    delete this.requestCache[requestStr]
                }
            }
            return null
        }
    }

    fetch(filters, fieldsOverride, relationsOverride, enableCache=true) {
        if (enableCache && !fieldsOverride && !relationsOverride && this.idFields && this.idFields.length > 0) {
            let normalizedFilters = normalizeFilter(filters)
            if (normalizedFilters.length === 1) {
                let filter = normalizedFilters[0]
                if (this.idFields.includes(filter.field)) {
                    if (!filter.op) {
                        let hit = this.idCacheLookup(filter.field, filter.value)
                        if (hit) return hit
                    } else if (filter.op === 'in') {
                        //  todo
                    }
                }
            }
        }

        let requestStr = this.buildRequestStr(filters, fieldsOverride, relationsOverride)

        if (enableCache) {
            let hit = this.requestCacheLookup(requestStr)
            if (hit) return hit
        }

        let thiz = this
        let promise = new Promise((resolve, reject) => {
            axios.get(requestStr).then(response => {
                resolve(thiz.parse(response.data.data))
            }, reject)
        })

        if (enableCache) {
            this.updateRequestCache(requestStr, promise)
            if (this.idFields && this.idFields.length > 0 && !fieldsOverride && !relationsOverride) {
                this.updateIdCache(promise)
            }
        }

        return promise
    }

    fetchOne(filters, fieldsOverride, relationsOverride, enableCache=true) {
        let thiz = this
        return new Promise((resolve, reject) => {
            thiz.fetch(filters, fieldsOverride, relationsOverride, enableCache)
            .then(result => {
                if (Array.isArray(result)) {
                    if (result.length > 0) {
                        resolve(result[0])
                    } else {
                        reject('item not found')
                    }
                } else if (result) {
                    resolve(result)
                } else {
                    reject('item not found')
                }
            }, reject)
        })
    }

    fetchLazy(filters, fieldsOverride, relationsOverride, enableCache = true) {
        // todo
        return this.fetch(filters, fieldsOverride, relationsOverride, enableCache)
    }
}
